// Reports (plan §7): the daily register a licensing inspector reads, late pickups, overrides and
// unknown-adult attempts, staff attendance. Computed in the browser from the sealed events
// (reports.ts); each exports CSV and the register prints one page per day.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Badge, Banner, Button, Table, Tabs, TextField } from "@vavr/ui";
import { api, FLAG_LABEL, Link } from "../shared/index.ts";
import { useConsole, useGet, download } from "./data.tsx";
import { Loading, Toolbar, today, useNames } from "./common.tsx";
import {
  clockLabel, dailyRegister, dateRange, exceptions, exceptionsCsv, fetchWindow, latePickups, lateCsv, registerCsv, staffAttendance, staffCsv, type EventLike,
} from "./reports.ts";

const PAGE = 1000;
const MAX_PAGES = 50;

/** Every event sealed in [since, until), newest first, following `before` cursors. */
export async function fetchAllEvents(base: string, since: number, until: number): Promise<{ events: EventLike[]; truncated: boolean }> {
  const out: EventLike[] = [];
  let before: number | null = null;
  for (let i = 0; i < MAX_PAGES; i++) {
    const r: { events: EventLike[] } = await api("GET", `${base}/events?since=${since}&until=${until}&limit=${PAGE}${before ? `&before=${before}` : ""}`);
    out.push(...r.events);
    if (r.events.length < PAGE) return { events: out, truncated: false };
    before = r.events[r.events.length - 1]!.seq;
  }
  return { events: out, truncated: true };
}

export function Reports() {
  const { base, site } = useConsole();
  const siteInfo = useGet<{ site: { settings: { closing_time: string | null } } }>(base, 60_000);
  const { names, children, ready } = useNames();
  // The range lives in the URL (?from=&to=) so a report can be bookmarked or shared.
  const [range, setRangeState] = useState(() => {
    const q = new URLSearchParams(typeof location === "undefined" ? "" : location.search);
    const ok = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);
    return { from: ok(q.get("from")) ?? today(site.tz), to: ok(q.get("to")) ?? ok(q.get("from")) ?? today(site.tz) };
  });
  const setRange = (r: { from: string; to: string }) => {
    setRangeState(r);
    try { history.replaceState(null, "", `${location.pathname}?from=${r.from}&to=${r.to}`); } catch { /* not in a browser */ }
  };
  const [data, setData] = useState<{ events: EventLike[]; truncated: boolean; key: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const days = dateRange(range.from, range.to);
  const valid = range.from && range.to && range.from <= range.to && days.length <= 31;
  const key = `${range.from}|${range.to}`;

  useEffect(() => {
    if (!valid) return;
    let live = true;
    setError(null);
    const w = fetchWindow(range.from, range.to, site.tz);
    fetchAllEvents(base, w.since, w.until).then((r) => live && setData({ ...r, key }), (e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [base, key, valid, site.tz]);

  const o = { from: range.from, to: range.to, tz: site.tz };
  const closing = siteInfo.data?.site.settings.closing_time ?? null;
  const computed = useMemo(() => {
    if (!data || data.key !== key || !ready) return null;
    const register = dailyRegister(data.events, children, names, o);
    return { register, late: latePickups(register, closing, site.tz), exc: exceptions(data.events, names, o), staff: staffAttendance(data.events, names, o) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, key, ready, children, names, closing]);

  const file = (what: string) => `vavr-${what}-${range.from}${range.to !== range.from ? `-to-${range.to}` : ""}.csv`;
  return (
    <div className="pmu-stack">
      <Toolbar actions={<Button variant="secondary" onClick={() => window.print()} disabled={!computed}>Print</Button>}>
        <TextField label="From" type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })} />
        <TextField label="To" type="date" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, to: e.target.value })} />
      </Toolbar>
      {!valid && <Banner tone="warning" title="Choose a range of up to 31 days." />}
      {error && <Banner tone="danger" title="The records could not be loaded">{error}</Banner>}
      {data?.truncated && <Banner tone="warning" title="This range has more records than one report can hold. Choose fewer days." />}
      {valid && !computed && !error && <Loading label="Building the reports" />}
      {computed && (
        <Tabs label="Reports" items={[
          {
            value: "register", label: "Daily register", content: (
              <div className="pmu-stack">
                <div className="pmu-row pmu-c-noprint"><Button size="sm" variant="secondary" onClick={() => download(file("register"), registerCsv(computed.register, site.tz))}>Export CSV</Button><span className="pmu-small pmu-muted">Times are when each handover happened, on the server's clock. Print gives one page per day.</span></div>
                {computed.register.map((d) => (
                  <section key={d.date} className="pmu-c-register-day" aria-label={`Register for ${d.date}`}>
                    <header className="pmu-c-register-head">
                      <h2>{site.name}: sign-in and sign-out register</h2>
                      <p>{new Intl.DateTimeFormat("en-US", { dateStyle: "full", timeZone: "UTC" }).format(new Date(`${d.date}T12:00:00Z`))}</p>
                    </header>
                    <Table
                      caption={`Register for ${d.date}`} hideCaption rowKey={(r) => r.child_id} rows={d.rows} empty="No children on the roster."
                      columns={[
                        { key: "c", header: "Child", rowHeader: true, render: (r) => r.name },
                        { key: "room", header: "Room", render: (r) => r.room },
                        { key: "in", header: "In", render: (r) => nw(lines(r.visits.map((v) => (v.in ? clockLabel(v.in.at, site.tz) : "—")))) },
                        { key: "inby", header: "Dropped off by", render: (r) => lines(r.visits.map((v) => v.in?.by ?? "")) },
                        { key: "out", header: "Out", render: (r) => nw(lines(r.visits.map((v) => (v.out ? clockLabel(v.out.at, site.tz) : v.in ? "Not recorded" : "—")))) },
                        { key: "outby", header: "Collected by", render: (r) => <span className="pmu-c-lines">{r.visits.map((v, i) => <span key={i}>{v.out?.by ?? "\u00a0"}{v.out?.flags.map((f) => <Badge key={f} tone={f === "restriction_hit" ? "restriction" : "warning"}>{FLAG_LABEL[f] ?? f}</Badge>)}</span>)}</span> },
                        { key: "staff", header: "Staff", render: (r) => nw(lines(r.visits.map((v) => [v.in?.staff, v.out?.staff].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(", ")))) },
                        { key: "notes", header: "Notes", render: (r) => (r.absent ? <Badge tone="warning">{r.notes.join("; ")}</Badge> : r.notes.join("; ")) },
                      ]}
                    />
                    <p className="pmu-c-register-sign">Checked by: ______________________ Date: __________</p>
                  </section>
                ))}
              </div>
            ),
          },
          {
            value: "late", label: `Late pickups (${computed.late.length})`, content: (
              <div className="pmu-stack">
                <div className="pmu-row"><Button size="sm" variant="secondary" disabled={!closing} onClick={() => download(file("late-pickups"), lateCsv(computed.late, site.tz))}>Export CSV</Button></div>
                {!closing ? <Banner tone="info" title="Set a closing time to see late pickups">Settings → Closing time.</Banner> : (
                  <Table
                    caption={`Pickups after ${closing}`} rowKey={(r) => `${r.date}-${r.child_id}-${r.at ?? "open"}`} rows={computed.late} empty="No late pickups."
                    columns={[
                      { key: "d", header: "Date", render: (r) => r.date },
                      { key: "c", header: "Child", rowHeader: true, render: (r) => <Link className="pmu-link" href={`/console/children/${r.child_id}`}>{r.name}</Link> },
                      { key: "t", header: "Picked up", render: (r) => (r.at ? clockLabel(r.at, site.tz) : <Badge tone="warning">No pickup recorded</Badge>) },
                      { key: "m", header: "Minutes late", align: "end", render: (r) => r.minutes_late ?? "" },
                      { key: "b", header: "Collected by", render: (r) => r.by ?? "" },
                      { key: "s", header: "Staff", render: (r) => r.staff ?? "" },
                    ]}
                  />
                )}
              </div>
            ),
          },
          {
            value: "exceptions", label: `Overrides and unknown adults (${computed.exc.length})`, content: (
              <div className="pmu-stack">
                <div className="pmu-row"><Button size="sm" variant="secondary" onClick={() => download(file("exceptions"), exceptionsCsv(computed.exc, site.tz))}>Export CSV</Button></div>
                <Table
                  caption="Overrides and flagged handovers" rowKey={(r) => String(r.seq)} rows={computed.exc} empty="None in this range."
                  columns={[
                    { key: "t", header: "When", render: (r) => nw(<Link className="pmu-link" href={`/console/events/${r.id}`}>{r.date} {clockLabel(r.at, site.tz)}</Link>) },
                    { key: "y", header: "What", render: (r) => nw(r.type) },
                    { key: "c", header: "Child", rowHeader: true, render: (r) => r.child },
                    { key: "a", header: "Adult", render: (r) => r.adult },
                    { key: "f", header: "Flags", render: (r) => <span className="pmu-row pmu-c-flags">{r.flags.map((l) => <Badge key={l} tone={l === FLAG_LABEL.restriction_hit ? "restriction" : "warning"}>{l}</Badge>)}</span> },
                    { key: "r", header: "Reason", render: (r) => r.reason },
                    { key: "s", header: "Staff", render: (r) => [r.staff, r.witness && `witness ${r.witness}`].filter(Boolean).join(", ") },
                  ]}
                />
              </div>
            ),
          },
          {
            value: "staff", label: "Staff attendance", content: (
              <div className="pmu-stack">
                <div className="pmu-row"><Button size="sm" variant="secondary" onClick={() => download(file("staff-attendance"), staffCsv(computed.staff, site.tz))}>Export CSV</Button></div>
                <Table
                  caption="Shifts" rowKey={(r) => `${r.date}-${r.staff_id}-${r.start ?? r.end}`} rows={computed.staff} empty="No shift check-ins in this range. Staff check in from the kiosk or the teacher app."
                  columns={[
                    { key: "d", header: "Date", render: (r) => r.date },
                    { key: "s", header: "Staff", rowHeader: true, render: (r) => r.staff },
                    { key: "a", header: "Start", render: (r) => (r.start ? clockLabel(r.start, site.tz) : <Badge tone="warning">Not recorded</Badge>) },
                    { key: "b", header: "End", render: (r) => (r.end ? clockLabel(r.end, site.tz) : <Badge tone="warning">Not recorded</Badge>) },
                    { key: "m", header: "Hours", align: "end", render: (r) => (r.minutes == null ? "" : (r.minutes / 60).toFixed(2)) },
                  ]}
                />
              </div>
            ),
          },
        ]} />
      )}
    </div>
  );
}

const nw = (x: ReactNode) => <span className="pmu-c-nowrap">{x}</span>;
const lines = (xs: string[]) => (xs.length === 0 ? "—" : xs.length === 1 ? xs[0] : <span className="pmu-c-lines">{xs.map((x, i) => <span key={i}>{x || " "}</span>)}</span>);

